import React, { ForwardedRef, useEffect, useRef, useState } from "react";

import { MeetingSessionMediaState, MeetingSessionStatus } from "@kalyzee/kast-web-sdk/http";
import { Meeting, MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";

import styles from "./MeetingPlayerDataOverlay.module.css";
import { ReactIcons } from "@kalyzee/kalyzee-ui";
import { UserAvatar } from "@kalyzee/kast-web-sdk/web-components";
import { getNormalUser } from "../../helpers/user";
import { UserCardAvatarArea } from "../common/UserCardAvatarArea";

export interface MeetingPlayerDataOverlayProps {
  meeting?: Meeting;
  session?: MeetingSession;
  mediaSession?: MeetingSessionMedia;
  mediaStream?: MediaStream;
  videoIsDisabled?: boolean;
  videoIsReady?: boolean;
  /**
   * The video comes from a local capture instead of a reception (own stream
   * previewed here, or pushed to a secondary screen): the media state then
   * describes the publication, so the wording follows.
   */
  local?: boolean;
  /**
   * The source has no camera / no mic to capture from. Reported by the picker,
   * not by the media state: an unplugged camera is replaced by an empty track,
   * so the publication stays `READY` and the player would show a black frame
   * with no explanation.
   */
  noVideoDevice?: boolean;
  noAudioDevice?: boolean;
  style?: React.CSSProperties;
  className?: string;
}

export interface MeetingPlayerDataOverlayRef {}

/**
 * `full` covers the player (nothing worth showing behind it), `badge` is a
 * discreet pill for states where the picture is still valid, `blur` and `none`
 * keep the video visible.
 *
 * A badge sits on the same black ground as the full overlay, except the
 * `danger` tone which turns it red.
 */
type OverlayTone = "danger";

type OverlayState =
  | { kind: "full"; message: string }
  | { kind: "badge"; message: string; tone?: OverlayTone }
  | { kind: "blur" }
  | { kind: "none" };

const resolveOverlayState = (
  session: MeetingSession,
  mediaSession: MeetingSessionMedia | undefined,
  mediaStream: MediaStream | undefined,
  videoIsDisabled: boolean | undefined,
  local: boolean | undefined,
  noVideoDevice: boolean | undefined,
  noAudioDevice: boolean | undefined
): OverlayState => {
  if (session.status !== MeetingSessionStatus.RUNNING) return { kind: "full", message: "Déconnecté" };
  if (!mediaSession) return { kind: "full", message: "Aucun média" };

  // A missing device outranks the media state: it says why the picture is
  // black, where the state only reports a publication that still looks fine
  if (noVideoDevice && noAudioDevice) return { kind: "full", message: "Aucun périphérique" };
  if (noVideoDevice) return { kind: "full", message: "Aucune caméra" };
  if (noAudioDevice) return { kind: "badge", message: "Aucun micro", tone: "danger" };

  switch (mediaSession.state) {
    // Coming up — NO_TRACKS included, reported while the publication has nothing
    // to send yet. A local capture already has a valid picture at that point, and
    // its state may stay behind (it is not recomputed for a disabled media), so
    // these must never hide it
    case MeetingSessionMediaState.WAITING:
    case MeetingSessionMediaState.CAPTURING:
    case MeetingSessionMediaState.NO_TRACKS:
      return local
        ? { kind: "badge", message: "Préparation du flux..." }
        : { kind: "full", message: "Préparation du flux vidéo." };
    case MeetingSessionMediaState.PUBLISHING:
      return local
        ? { kind: "badge", message: "Publication du flux..." }
        : { kind: "full", message: "Publication en cours..." };
    case MeetingSessionMediaState.NO_VIDEO_TRACK:
      return { kind: "full", message: local ? "Vidéo non publiée" : "Vidéo non reçue" };
    case MeetingSessionMediaState.NO_AUDIO_TRACK:
      // The picture is fine, only the audio is missing: don't hide it
      return { kind: "badge", message: local ? "Audio non publié" : "Audio non reçu" };
    // A local capture keeps showing its picture: a stopped or failed publication
    // is reported without hiding what the camera still sees
    case MeetingSessionMediaState.STOPPED:
      return local
        ? { kind: "badge", message: "Flux arrêté", tone: "danger" }
        : { kind: "full", message: "Flux arrêté" };
    case MeetingSessionMediaState.ERROR:
      return local
        ? { kind: "badge", message: "Erreur de publication", tone: "danger" }
        : { kind: "full", message: "Erreur du flux distant" };
    case MeetingSessionMediaState.READY:
      // A local preview always has its stream: only a reception can be pending
      if (!mediaStream) return { kind: "full", message: "Récupération en cours..." };
      if (!mediaSession.video.enabled) return { kind: "full", message: "La caméra est désactivée" };
      if (videoIsDisabled) return { kind: "blur" };
      return { kind: "none" };
    default:
      // Unknown state: a reception has nothing to show, a local capture has
      return local ? { kind: "none" } : { kind: "full", message: "Impossible de récupérer le flux vidéo." };
  }
};

/** How long the "stream is live" confirmation stays on screen. */
const READY_FLASH_DURATION = 1000;

/**
 * Confirms the stream reaching READY for a moment, then gets out of the way.
 * Only a transition is worth showing: a player mounted on an already ready
 * media has nothing to announce.
 */
const useReadyFlash = (ready: boolean) => {
  const [flashing, setFlashing] = useState(false);
  const previousRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = ready;
    if (previous === undefined || previous === ready || !ready) return;
    setFlashing(true);
    const timeout = setTimeout(() => setFlashing(false), READY_FLASH_DURATION);
    return () => clearTimeout(timeout);
  }, [ready]);

  return flashing;
};

export const MeetingPlayerDataOverlay = React.forwardRef(
  (
    {
      meeting,
      session,
      mediaSession,
      mediaStream,
      videoIsDisabled,
      local,
      noVideoDevice,
      noAudioDevice,
      style,
      className,
    }: MeetingPlayerDataOverlayProps,
    forwardRef: ForwardedRef<MeetingPlayerDataOverlayRef>
  ) => {
    // Emission only: confirming a publication is useful feedback on our own
    // stream, a reception going live is not something to announce
    const flashingReady = useReadyFlash(!!local && mediaSession?.state === MeetingSessionMediaState.READY && !!mediaStream);

    if (!meeting || !session) return null;
    const { avatar, displayName } = session.user ? getNormalUser(session.user) : { avatar: undefined, displayName: undefined };

    const state = resolveOverlayState(session, mediaSession, mediaStream, videoIsDisabled, local, noVideoDevice, noAudioDevice);
    if (state.kind === "none") {
      if (!flashingReady) return null;
      return (
        <div className={`${styles.badge} ${className ?? ""}`} style={style}>
          <ReactIcons.Checkmark className={styles.badgeIcon} />
          En direct
        </div>
      );
    }
    if (state.kind === "blur") return <div className={styles.notReadyOverlay} />;

    if (state.kind === "badge") {
      const toneClass = state.tone === "danger" ? styles.danger : "";
      return <div className={`${styles.badge} ${toneClass} ${className ?? ""}`} style={style}>{state.message}</div>;
    }

    return (
      <div className={`${styles.container} ${className ?? ""}`} style={style}>
        <UserCardAvatarArea user={session.user ? { id: session.user.id, username: displayName, avatar } : undefined}>
          <UserAvatar style={{ width: "60px", height: "60px" }} image={avatar} username={displayName} />
        </UserCardAvatarArea>
        {state.message}
      </div>
    );
  }
);
